// src/features/purchasing2/Purchase2Page.jsx
import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { Purchasing2Api } from './api';
import { BuildCart } from './components/BuildCart';
import { ReviewGroup } from './components/ReviewGroup';
import { SendPanel } from './components/SendPanel';
import { ReceivePanel } from './components/ReceivePanel';

const TABS = [
  { id: 'build-cart', name: 'Build Cart', description: 'Add items to your purchase order' },
  { id: 'review', name: 'Review', description: 'Review and approve purchase orders' },
  { id: 'send', name: 'Send', description: 'Send purchase orders to vendors' },
  { id: 'receive', name: 'Receive', description: 'Receive and process deliveries' },
];

export default function Purchase2Page() {
  const [selectedPOs, setSelectedPOs] = useState([]);
  const [activeTab, setActiveTab] = useState('build-cart');
  const [completedSteps, setCompletedSteps] = useState(new Set());

  // Load bootstrap data
  const { data: bootstrapData, isLoading, error } = useQuery(
    'purchasing2-bootstrap',
    () => Purchasing2Api.getBootstrap().then(res => res.data),
    {
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 10 * 60 * 1000, // 10 minutes
    }
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-md p-4">
        <div className="flex">
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">
              Error loading purchasing data
            </h3>
            <div className="mt-2 text-sm text-red-700">
              {error instanceof Error ? error.message : 'Unknown error occurred'}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!bootstrapData) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-500">No bootstrap data available</p>
      </div>
    );
  }

  const handlePOsCreated = (newPOs) => {
    console.log('POs created:', newPOs);
    // In a real implementation, this would update the purchase orders list
  };

  const handlePOsSelected = (pos) => {
    setSelectedPOs(pos);
  };

  const handlePOsUpdated = (updatedPOs) => {
    setSelectedPOs(updatedPOs);
    // In a real implementation, this would refresh the purchase orders list
  };

  const handleStepComplete = (stepId) => {
    setCompletedSteps(prev => new Set([...prev, stepId]));
  };

  const canAccessTab = (tabId) => {
    const tabIndex = TABS.findIndex(tab => tab.id === tabId);
    const currentTabIndex = TABS.findIndex(tab => tab.id === activeTab);
    
    // Always allow access to the first tab (build-cart)
    if (tabIndex === 0) return true;
    
    // Allow access to current tab and previous tabs
    return tabIndex <= currentTabIndex;
  };

  // Mock purchase orders data for now
  const mockPurchaseOrders = [
    {
      id: 1,
      po: 'PO-001',
      status: 'DRAFT',
      vendor: 'Test Vendor',
      vendorName: 'Test Vendor Inc.',
      timestamp: new Date().toISOString(),
      items: [{ id: 1, quantity: 5, received: 0 }],
    },
  ];

  const renderTabContent = () => {
    const commonProps = {
      bootstrapData,
      onStepComplete: handleStepComplete,
    };

    switch (activeTab) {
      case 'build-cart':
        return (
          <BuildCart
            {...commonProps}
            onPOsCreated={handlePOsCreated}
          />
        );
      case 'review':
        return (
          <ReviewGroup
            {...commonProps}
            purchaseOrders={mockPurchaseOrders}
            selectedPOs={selectedPOs}
            onPOsSelected={handlePOsSelected}
          />
        );
      case 'send':
        return (
          <SendPanel
            {...commonProps}
            purchaseOrders={mockPurchaseOrders}
            onPOsUpdated={handlePOsUpdated}
          />
        );
      case 'receive':
        return (
          <ReceivePanel
            {...commonProps}
            purchaseOrders={mockPurchaseOrders}
            onPOsUpdated={handlePOsUpdated}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Tab Navigation */}
      <div className="border-b border-gray-200 mb-8">
        <nav className="-mb-px flex space-x-8">
          {TABS.map((tab, index) => {
            const isCompleted = completedSteps.has(tab.id);
            const isAccessible = canAccessTab(tab.id);
            const isActive = activeTab === tab.id;
            
            return (
              <button
                key={tab.id}
                onClick={() => isAccessible && setActiveTab(tab.id)}
                disabled={!isAccessible}
                className={`
                  py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap flex items-center space-x-2
                  ${
                    isActive
                      ? 'border-blue-500 text-blue-600'
                      : isAccessible
                      ? 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      : 'border-transparent text-gray-300 cursor-not-allowed'
                  }
                `}
              >
                <span>{tab.name}</span>
                {isCompleted && (
                  <svg className="w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                )}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Tab Description and Progress */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-600">
            {TABS.find(tab => tab.id === activeTab)?.description}
          </p>
        </div>
        <div className="flex items-center space-x-4">
          {/* Progress indicator */}
          <div className="flex items-center space-x-2">
            <span className="text-sm text-gray-500">Progress:</span>
            <div className="flex space-x-1">
              {TABS.map((tab, index) => (
                <div
                  key={tab.id}
                  className={`
                    w-3 h-3 rounded-full
                    ${
                      completedSteps.has(tab.id)
                        ? 'bg-green-500'
                        : index <= TABS.findIndex(t => t.id === activeTab)
                        ? 'bg-blue-500'
                        : 'bg-gray-300'
                    }
                  `}
                />
              ))}
            </div>
          </div>
          
          {/* Navigation buttons */}
          <div className="flex space-x-2">
            <button
              onClick={() => {
                const currentIndex = TABS.findIndex(tab => tab.id === activeTab);
                if (currentIndex > 0) {
                  setActiveTab(TABS[currentIndex - 1].id);
                }
              }}
              disabled={TABS.findIndex(tab => tab.id === activeTab) === 0}
              className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <button
              onClick={() => {
                const currentIndex = TABS.findIndex(tab => tab.id === activeTab);
                if (currentIndex < TABS.length - 1) {
                  setActiveTab(TABS[currentIndex + 1].id);
                }
              }}
              disabled={TABS.findIndex(tab => tab.id === activeTab) === TABS.length - 1}
              className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {/* Tab Content */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-6">
          {renderTabContent()}
        </div>
      </div>
    </div>
  );
}
